// 'use client';
// import React, { useState, useEffect, useRef } from 'react';
// import {
//   ArrowUpDown,
//   ChevronDown,
//   ChevronUp,
//   Search,
//   Trash2,
//   Edit,
//   GripVertical,
//   Lock
// } from 'lucide-react';
// import { Column } from './SortableTableProps.d';
// import DeleteConfirmationModal from '@/components/Modal/DeleteConfirmationModal';
// import Pagination from '../Pagination';
// import useDebounce from '@/hooks/useDebounce';
// import Button from '../Button';

// // Base interface for the data object
// export interface BaseRecord {
//   uuid: string;
//   col_order?: number;
//   isDraggable?: boolean; // Add isDraggable property to allow some rows to be non-draggable
//   [key: string]: unknown;
// }

// type SortDirection = 'asc' | 'desc' | null;

// interface SortConfig<T> {
//   key: keyof T | null;
//   direction: SortDirection;
// }

// // Improved table props interface
// interface ExtendedTableProps<T extends BaseRecord> {
//   data: T[];
//   columns: Column<T>[];
//   className?: string;
//   onEdit?: (row: T) => void;
//   onDelete?: (row: T) => Promise<void>;
//   itemsPerPage?: number;
//   currentPage?: number;
//   totalCount?: number;
//   onPageChange?: (page: number) => void;
//   onSearch?: (searchTerm: string) => Promise<void>;
//   sortConfig?: SortConfig<T>;
//   onSort?: (key: keyof T) => void;
//   searchTerm?: string;
//   // Drag and drop related props
//   draggable?: boolean;
//   onReorder?: (reorderedData: T[]) => void;
//   // Prop for saving column order
//   onSaveColumnOrder?: (orderData: { columns: { uuid: string; col_order: number }[] }) => Promise<void>;
// }

// interface DeleteModalState<T> {
//   isOpen: boolean;
//   itemToDelete: T | null;
// }

// const SortableTable = <T extends BaseRecord>({
//   data,
//   columns,
//   className = '',
//   onEdit,
//   onDelete,
//   itemsPerPage = 10,
//   currentPage = 1,
//   totalCount = 0,
//   onPageChange,
//   onSearch,
//   sortConfig,
//   onSort,
//   searchTerm = '',
//   draggable = false,
//   onReorder,
//   onSaveColumnOrder,
// }: ExtendedTableProps<T>): React.ReactElement => {
//   const [localData, setLocalData] = useState<T[]>(data);
//   const [fullDataSet, setFullDataSet] = useState<T[]>(data); // Store the full dataset
//   const [localSearchTerm, setLocalSearchTerm] = useState<string>(searchTerm);
//   const [dragItem, setDragItem] = useState<number | null>(null);
//   const [dragOverItem, setDragOverItem] = useState<number | null>(null);
//   // Track if the order has been changed
//   const [orderChanged, setOrderChanged] = useState<boolean>(false);
//   // Track if a save is in progress
//   const [isSaving, setIsSaving] = useState<boolean>(false);
//   const debouncedSearchTerm = useDebounce<string>(localSearchTerm, 1000);
//   // Use a ref to compare data changes
//   const previousDataRef = useRef<T[]>([]);

//   // Update local data when prop data changes
//   useEffect(() => {
//     // Only update and reset if data actually changed OR the order has changed
//     const itemsChanged = JSON.stringify(data.map(item => item.uuid)) !==
//       JSON.stringify(localData.map(item => item.uuid));

//     // Also check if the order of the same items has changed
//     const orderChanged = !itemsChanged && !compareArraysByOrderAndUuid(data, localData);

//     if (itemsChanged || orderChanged) {
//       console.log("Data or order changed, updating local data");
//       setLocalData(data);
//       setOrderChanged(false);
//       setFullDataSet(data);
//     }
//   }, [data, localData]);

//   // Add this helper function
//   const compareArraysByOrderAndUuid = (arr1: T[], arr2: T[]): boolean => {
//     if (arr1.length !== arr2.length) return false;

//     // Check if the UUIDs appear in the same order
//     for (let i = 0; i < arr1.length; i++) {
//       if (arr1[i].uuid !== arr2[i].uuid) {
//         return false;
//       }
//     }
//     return true;
//   };

//   // Handle search with debounce
//   useEffect(() => {
//     if (onSearch && debouncedSearchTerm !== searchTerm) {
//       // Before searching, update the fullDataSet with current order changes
//       updateFullDataSetWithCurrentOrder();

//       onSearch(debouncedSearchTerm).catch((error: Error) => {
//         console.error('Search error:', error);
//       });
//     }
//   }, [debouncedSearchTerm, onSearch, searchTerm]);

//   useEffect(() => {
//     setLocalSearchTerm(searchTerm);
//   }, [searchTerm]);

//   // Function to update the full dataset with current visible ordering
//   const updateFullDataSetWithCurrentOrder = () => {
//     // Create a map of uuid -> col_order from the current visible data
//     const orderMap = new Map();
//     localData.forEach(item => {
//       orderMap.set(item.uuid, item.col_order);
//     });

//     // Update the full dataset with these orders where applicable
//     setFullDataSet(prevFullDataSet =>
//       prevFullDataSet.map(item => {
//         if (orderMap.has(item.uuid)) {
//           return {
//             ...item,
//             col_order: orderMap.get(item.uuid)
//           };
//         }
//         return item;
//       })
//     );
//   };

//   const handleSearch = (e: React.ChangeEvent<HTMLInputElement>): void => {
//     const value = e.target.value;

//     // Before changing the search term, save the current ordering to fullDataSet
//     updateFullDataSetWithCurrentOrder();

//     setLocalSearchTerm(value);

//     // If no debounce hook is being used, trigger search immediately 
//     if (!useDebounce && onSearch) {
//       onSearch(value).catch((error: Error) => {
//         console.error('Search error:', error);
//       });
//     }
//   };

//   const [deleteModalState, setDeleteModalState] = useState<DeleteModalState<T>>({
//     isOpen: false,
//     itemToDelete: null
//   });

//   const compareArrays = (arr1: T[], arr2: T[]): boolean => {
//     if (arr1.length !== arr2.length) return false;

//     // Create arrays of objects with just uuid and col_order for comparison
//     const simplified1 = arr1.map(item => ({
//       uuid: item.uuid,
//       col_order: item.col_order
//     }));
//     const simplified2 = arr2.map(item => ({
//       uuid: item.uuid,
//       col_order: item.col_order
//     }));

//     // Check if arrays are different by comparing stringified versions
//     return JSON.stringify(simplified1) === JSON.stringify(simplified2);
//   };

//   // Drag and drop handlers
//   const handleDragStart = (e: React.DragEvent<HTMLTableRowElement>, index: number) => {
//     if (!draggable) return;

//     // Check if this item is draggable
//     const item = localData[index];
//     if (item.isDraggable === false) {
//       e.preventDefault();
//       return;
//     }

//     setDragItem(index);
//     // Store the index to make it available during drag operation
//     e.dataTransfer.setData("text/plain", index.toString());

//     // Save a reference to the current element and apply styles directly
//     const currentElement = e.currentTarget;
//     if (currentElement) {
//       currentElement.classList.add('opacity-50');
//     }
//   };

//   const handleDragOver = (e: React.DragEvent<HTMLTableRowElement>, index: number) => {
//     e.preventDefault(); // This is crucial to allow dropping

//     if (!draggable || dragItem === null) return;

//     // Check if the target item is draggable
//     const targetItem = localData[index];
//     if (targetItem.isDraggable === false) {
//       return;
//     }

//     try {
//       // Determine drop position with visual indicator
//       const targetRow = e.currentTarget;
//       const bounding = targetRow.getBoundingClientRect();
//       const mouseY = e.clientY;
//       const mousePosition = mouseY - bounding.top;
//       const isAboveHalf = mousePosition < bounding.height / 2;

//       // Apply visual feedback based on position
//       targetRow.classList.remove('border-t-4', 'border-b-4', 'border-blue-500');
//       if (isAboveHalf) {
//         targetRow.classList.add('border-t-4', 'border-blue-500');
//       } else {
//         targetRow.classList.add('border-b-4', 'border-blue-500');
//       }
//     } catch (error) {
//       console.error("Error in dragOver:", error);
//     }

//     setDragOverItem(index);
//   };

//   const handleDragDrop = (e: React.DragEvent<HTMLTableRowElement>) => {
//     e.preventDefault();
//     e.stopPropagation();

//     if (!draggable || dragItem === null || dragOverItem === null) return;

//     // Don't allow dropping on non-draggable items
//     const targetItem = localData[dragOverItem];
//     if (targetItem.isDraggable === false) {
//       setDragItem(null);
//       setDragOverItem(null);
//       return;
//     }

//     // Clear any drag over styling
//     try {
//       document.querySelectorAll('tr').forEach(row => {
//         row.classList.remove('opacity-50', 'border-t-4', 'border-b-4', 'border-blue-500');
//       });
//     } catch (error) {
//       console.error("Error cleaning up styles:", error);
//     }

//     // Don't do anything if the item is dropped at the same position
//     if (dragItem === dragOverItem) {
//       setDragItem(null);
//       setDragOverItem(null);
//       return;
//     }

//     console.log(`Reordering: Moving item from position ${dragItem} to position ${dragOverItem}`);

//     // Create a copy of the data
//     const newData = [...localData];
//     const draggedItem = newData[dragItem];

//     // Don't allow dragging a non-draggable item
//     if (draggedItem.isDraggable === false) {
//       setDragItem(null);
//       setDragOverItem(null);
//       return;
//     }

//     // Remove the item from its original position
//     newData.splice(dragItem, 1);

//     // Insert the item at the new position
//     newData.splice(dragOverItem, 0, draggedItem);

//     // Find all draggable and non-draggable items
//     const draggableItems = newData.filter(item => item.isDraggable !== false);
//     const nonDraggableItems = newData.filter(item => item.isDraggable === false);

//     // Update col_order only for draggable items, preserving the order of non-draggable items
//     let draggableIndex = 1;

//     // Create the final array in the correct order
//     const updatedData = newData.map(item => {
//       if (item.isDraggable === false) {
//         // Non-draggable items keep their existing col_order
//         return item;
//       } else {
//         // Draggable items get sequential col_order values
//         const newItem = {
//           ...item,
//           col_order: draggableIndex++
//         };
//         return newItem;
//       }
//     });

//     // Log the reordering details for debugging
//     console.log("Original order:", localData.map(item => `${item.uuid}: ${item.col_order}`));
//     console.log("New order:", updatedData.map(item => `${item.uuid}: ${item.col_order}`));

//     // Check if the order has actually changed
//     const orderActuallyChanged = !compareArrays(localData, updatedData);
//     console.log("Order actually changed:", orderActuallyChanged);

//     if (orderActuallyChanged) {
//       // Update local state with the data including updated col_order values
//       setLocalData(updatedData);

//       // Also update the order in the full dataset
//       updateFullDataSetWithVisibleOrder(updatedData);

//       // Force set the orderChanged flag to true
//       console.log("Setting orderChanged to true after drag and drop");
//       setOrderChanged(true);

//       // Call onReorder callback if provided
//       if (onReorder) {
//         onReorder(updatedData);
//       }
//     }

//     // Reset drag states
//     setDragItem(null);
//     setDragOverItem(null);
//   };

//   const updateFullDataSetWithVisibleOrder = (visibleData: T[]) => {
//     const orderMap = new Map();
//     visibleData.forEach(item => {
//       orderMap.set(item.uuid, item.col_order);
//     });

//     setFullDataSet(prevFullDataSet =>
//       prevFullDataSet.map(item => {
//         if (orderMap.has(item.uuid)) {
//           return {
//             ...item,
//             col_order: orderMap.get(item.uuid)
//           };
//         }
//         return item;
//       })
//     );
//   };

//   const handleDragEnd = (e: React.DragEvent<HTMLTableRowElement>) => {
//     // Clear any styling applied during dragging
//     try {
//       if (e.currentTarget) {
//         e.currentTarget.classList.remove('opacity-50');
//       }

//       document.querySelectorAll('tr').forEach(row => {
//         row.classList.remove('opacity-50', 'border-t-4', 'border-b-4', 'border-blue-500');
//       });
//     } catch (error) {
//       console.error("Error cleaning up styles on drag end:", error);
//     }

//     setDragItem(null);
//     setDragOverItem(null);
//   };

//   const handleDelete = (row: T): void => {
//     setDeleteModalState({
//       isOpen: true,
//       itemToDelete: row
//     });
//   };

//   const handleCloseDeleteModal = (): void => {
//     setDeleteModalState({
//       isOpen: false,
//       itemToDelete: null
//     });
//   };

//   // Function to save column order
//   const handleSaveColumnOrder = async (): Promise<void> => {
//     if (!onSaveColumnOrder) return;

//     setIsSaving(true);
//     try {
//       // Important: Use the original data prop to ensure ALL columns are included
//       // regardless of current search/filter state
//       const allData = data;

//       // Create a map of UUID to new position from current view
//       const visibleOrderMap = new Map();
//       localData.forEach((item, index) => {
//         visibleOrderMap.set(item.uuid, index + 1);
//       });

//       // Create the order data with ALL columns
//       const orderData = {
//         columns: allData.map(item => {
//           // Use the new order for visible items, maintain existing order for others
//           const newOrder = visibleOrderMap.has(item.uuid)
//             ? visibleOrderMap.get(item.uuid)
//             : (typeof item.col_order === 'number' ? item.col_order : 0);

//           return {
//             uuid: item.uuid,
//             col_order: newOrder
//           };
//         })
//       };

//       console.log("Saving ALL columns. Total count:", orderData.columns.length);
//       console.log("Currently visible columns:", localData.length);

//       // Call the provided save function with ALL columns
//       await onSaveColumnOrder(orderData);

//       // Reset the order changed flag after successful save
//       setOrderChanged(false);
//     } catch (error) {
//       console.error('Error saving column order:', error);
//     } finally {
//       setIsSaving(false);
//     }
//   };

//   // Helper for getting nested values
//   const getNestedValue = <K extends keyof T>(
//     obj: T,
//     key: K,
//     valueAccessor?: string
//   ): T[K] | string | null => {
//     if (!obj) return null;
//     const value = obj[key];

//     if (valueAccessor && typeof value === 'object' && value !== null) {
//       return (value as Record<string, unknown>)[valueAccessor] as string;
//     }
//     return value;
//   };

//   // Render cell content based on type and config
//   const renderCellContent = (value: unknown, row: T, column: Column<T>): React.ReactNode => {
//     if (column.render) {
//       return column.render(value, row);
//     }
//     if (column.customRender) {
//       return column.customRender(value);
//     }

//     const actualValue = getNestedValue(row, column.key, column.valueAccessor);

//     if (typeof actualValue === 'boolean') {
//       return actualValue ? 'Yes' : 'No';
//     }

//     if (actualValue === null || actualValue === undefined || actualValue === '') {
//       return '-';
//     }

//     if (typeof actualValue === 'object' && actualValue !== null) {
//       if (Array.isArray(actualValue)) {
//         return actualValue.join(', ');
//       }

//       return Object.entries(actualValue as Record<string, boolean>)
//         .filter(([, value]) => value === true)
//         .map(([key]) => key)
//         .join(', ');
//     }

//     return String(actualValue);
//   };

//   const totalPages = Math.ceil(totalCount / itemsPerPage);

//   return (
//     <>
//       <div className={`space-y-2 ${className} mt-2`}>
//         <div className="flex justify-end gap-2 items-center">
//           {onSearch && (
//             <div className="flex px-4 items-center justify-center bg-white border rounded-md">
//               <Search className="text-gray-400 h-4 w-4" />
//               <input
//                 type="text"
//                 placeholder="Search..."
//                 className="pl-2 pr-4 py-2 focus:outline-none"
//                 value={localSearchTerm}
//                 onChange={handleSearch}
//               />
//             </div>
//           )}

//           {/* Save Order Button - always shown when draggable is true */}
//           {draggable && onSaveColumnOrder && orderChanged && (
//             <Button
//               btnStyle="brandBtn"
//               className="flex items-center gap-2 px-4 py-1.5"
//               onClick={handleSaveColumnOrder}
//               disabled={isSaving}
//             >
//               {isSaving ? 'Saving...' : 'Save Order'}
//             </Button>
//           )}

//         </div>

//         <div className="overflow-x-auto border rounded-lg">
//           <table className="min-w-full divide-y divide-gray-200 background-elements">
//             <thead className="bg-gray-100 dark:bg-gray-800">
//               <tr>
//                 {draggable && (
//                   <th className="w-12 px-2 py-3"></th>
//                 )}
//                 {columns.map(column => (
//                   <th
//                     key={String(column.key)}
//                     className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
//                     onClick="Text"
//                   >
//                     <div className="flex items-center gap-2">
//                       {column.label}
//                       {column.sortable && sortConfig && (
//                         sortConfig.key === column.key
//                           ? (sortConfig.direction === 'asc'
//                             ? <ChevronUp className="h-4 w-4" />
//                             : <ChevronDown className="h-4 w-4" />
//                           )
//                           : <ArrowUpDown className="h-4 w-4" />
//                       )}
//                     </div>
//                   </th>
//                 ))}
//                 {(onEdit || onDelete) && (
//                   <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
//                     Actions
//                   </th>
//                 )}
//               </tr>
//             </thead>
//             <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200">
//               {localData.map((row, index) => (
//                 <tr
//                   key={row.uuid || index}
//                   className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
//                   draggable={draggable && row.isDraggable !== false}
//                   onDragStart="Text"
//                   onDragOver={(e) => handleDragOver(e, index)}
//                   onDrop={handleDragDrop}
//                   onDragEnd={handleDragEnd}
//                   onDragEnter={(e) => {
//                     if (draggable) e.preventDefault();
//                   }}
//                   onDragLeave={(e) => {
//                     if (draggable && e.currentTarget) {
//                       try {
//                         e.currentTarget.classList.remove('border-t-4', 'border-b-4', 'border-blue-500');
//                       } catch (error) {
//                         console.error("Error in dragLeave:", error);
//                       }
//                     }
//                   }}
//                 >
//                   {draggable && (
//                     <td className="w-12 px-2 py-3 text-center">
//                       {row.isDraggable === false ? (
//                         <div className="text-gray-400">
//                           <Lock className="h-5 w-5" />
//                         </div>
//                       ) : (
//                         <div className="cursor-grab text-gray-400">
//                           <GripVertical className="h-5 w-5" />
//                         </div>
//                       )}
//                     </td>
//                   )}
//                   {columns.map(column => (
//                     <td
//                       key={String(column.key)}
//                       className="px-4 py-3 text-sm text-gray-900 dark:text-gray-200 whitespace-nowrap"
//                     >
//                       "Text"
//                     </td>
//                   ))}
//                   {(onEdit || onDelete) && (
//                     <td className="px-4 py-3 text-right">
//                       <div className="flex justify-end space-x-2">
//                         {onEdit && (
//                           <button
//                             onClick="Text"
//                             className="text-brandColor hover:text-blue-800 p-1 rounded"
//                           >
//                             <Edit className="h-4 w-4" />
//                           </button>
//                         )}
//                         {onDelete && (
//                           <button
//                             onClick={() => handleDelete(row)}
//                             className="text-red-600 hover:text-red-800 p-1 rounded"
//                           >
//                             <Trash2 className="h-4 w-4" />
//                           </button>
//                         )}
//                       </div>
//                     </td>
//                   )}
//                 </tr>
//               ))}
//             </tbody>
//           </table>
//         </div>

//         {totalCount > itemsPerPage && onPageChange && (
//           <div className="mt-4">
//             <Pagination
//               currentPage={currentPage}
//               totalPages={totalPages}
//               onPageChange={onPageChange}
//             />
//           </div>
//         )}
//       </div>

//       {deleteModalState.itemToDelete && (
//         <DeleteConfirmationModal
//           entityId={deleteModalState.itemToDelete.uuid}
//           entityType="role"
//           deleteFunction={async () => {
//             if (onDelete && deleteModalState.itemToDelete) {
//               await onDelete(deleteModalState.itemToDelete);
//             }
//           }}
//           isOpen={deleteModalState.isOpen}
//           onClose={handleCloseDeleteModal}
//         />
//       )}
//     </>
//   );
// };

// export default SortableTable;

import React, { useState, useEffect } from 'react';
import {
  ArrowUpDown,
  ChevronDown,
  ChevronUp,
  Search,
  Trash2,
  Edit,
  GripVertical,
  Lock
} from 'lucide-react';
import { Column } from './SortableTableProps.d';
import DeleteConfirmationModal from '@/components/Modal/DeleteConfirmationModal';
import Pagination from '../Pagination';
import useDebounce from '@/hooks/useDebounce';
import Button from '../Button';

// Base interface for the data object
export interface BaseRecord {
  uuid: string;
  col_order?: number;
  isDraggable?: boolean; // Add isDraggable property to allow some rows to be non-draggable
  [key: string]: unknown;
}

type SortDirection = 'asc' | 'desc' | null;

interface SortConfig<T> {
  key: keyof T | null;
  direction: SortDirection;
}

// Improved table props interface
interface ExtendedTableProps<T extends BaseRecord> {
  data: T[];
  columns: Column<T>[];
  className?: string;
  onEdit?: (row: T) => void;
  onDelete?: (row: T) => Promise<void>;
  itemsPerPage?: number;
  currentPage?: number;
  totalCount?: number;
  onPageChange?: (page: number) => void;
  onSearch?: (searchTerm: string) => Promise<void>;
  sortConfig?: SortConfig<T>;
  onSort?: (key: keyof T) => void;
  searchTerm?: string;
  // Drag and drop related props
  draggable?: boolean;
  onReorder?: (reorderedData: T[]) => void;
  // Prop for saving column order
  onSaveColumnOrder?: (orderData: { columns: { uuid: string; col_order: number }[] }) => Promise<void>;
}

interface DeleteModalState<T> {
  isOpen: boolean;
  itemToDelete: T | null;
}

const SortableTable = <T extends BaseRecord>({
  data,
  columns,
  className = '',
  onEdit,
  onDelete,
  itemsPerPage = 10,
  currentPage = 1,
  totalCount = 0,
  onPageChange,
  onSearch,
  sortConfig,
  searchTerm = '',
  draggable = false,
  onReorder,
  onSaveColumnOrder,
}: ExtendedTableProps<T>): React.ReactElement => {
  const [localData, setLocalData] = useState<T[]>(data);
  const [localSearchTerm, setLocalSearchTerm] = useState<string>(searchTerm);
  const [dragItem, setDragItem] = useState<number | null>(null);
  const [dragOverItem, setDragOverItem] = useState<number | null>(null);
  // Track if the order has been changed
  const [orderChanged, setOrderChanged] = useState<boolean>(false);
  // Track if a save is in progress
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const debouncedSearchTerm = useDebounce<string>(localSearchTerm, 1000);

  // Update local data when prop data changes
  useEffect(() => {
    // Only update and reset if data actually changed OR the order has changed
    const itemsChanged = JSON.stringify(data.map(item => item.uuid)) !==
      JSON.stringify(localData.map(item => item.uuid));

    // Also check if the order of the same items has changed
    const orderChanged = !itemsChanged && !compareArraysByOrderAndUuid(data, localData);

    if (itemsChanged || orderChanged) {
      console.log("Data or order changed, updating local data");
      setLocalData(data);
      setOrderChanged(false);
    }
  }, [data, localData]);

  // Add this helper function
  const compareArraysByOrderAndUuid = (arr1: T[], arr2: T[]): boolean => {
    if (arr1.length !== arr2.length) return false;

    // Check if the UUIDs appear in the same order
    for (let i = 0; i < arr1.length; i++) {
      if (arr1[i].uuid !== arr2[i].uuid) {
        return false;
      }
    }
    return true;
  };

  // Handle search with debounce
  useEffect(() => {
    if (onSearch && debouncedSearchTerm !== searchTerm) {
      onSearch(debouncedSearchTerm).catch((error: Error) => {
        console.error('Search error:', error);
      });
    }
  }, [debouncedSearchTerm, onSearch, searchTerm]);

  useEffect(() => {
    setLocalSearchTerm(searchTerm);
  }, [searchTerm]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value;

    setLocalSearchTerm(value);

    // If no debounce hook is being used, trigger search immediately 
    if (!useDebounce && onSearch) {
      onSearch(value).catch((error: Error) => {
        console.error('Search error:', error);
      });
    }
  };

  const [deleteModalState, setDeleteModalState] = useState<DeleteModalState<T>>({
    isOpen: false,
    itemToDelete: null
  });

  const compareArrays = (arr1: T[], arr2: T[]): boolean => {
    if (arr1.length !== arr2.length) return false;

    // Create arrays of objects with just uuid and col_order for comparison
    const simplified1 = arr1.map(item => ({
      uuid: item.uuid,
      col_order: item.col_order
    }));
    const simplified2 = arr2.map(item => ({
      uuid: item.uuid,
      col_order: item.col_order
    }));

    // Check if arrays are different by comparing stringified versions
    return JSON.stringify(simplified1) === JSON.stringify(simplified2);
  };

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent<HTMLTableRowElement>, index: number) => {
    e.preventDefault(); // This is crucial to allow dropping

    if (!draggable || dragItem === null) return;

    // Check if the target item is draggable
    const targetItem = localData[index];
    if (targetItem.isDraggable === false) {
      return;
    }

    try {
      // Determine drop position with visual indicator
      const targetRow = e.currentTarget;
      const bounding = targetRow.getBoundingClientRect();
      const mouseY = e.clientY;
      const mousePosition = mouseY - bounding.top;
      const isAboveHalf = mousePosition < bounding.height / 2;

      // Apply visual feedback based on position
      targetRow.classList.remove('border-t-4', 'border-b-4', 'border-blue-500');
      if (isAboveHalf) {
        targetRow.classList.add('border-t-4', 'border-blue-500');
      } else {
        targetRow.classList.add('border-b-4', 'border-blue-500');
      }
    } catch (error) {
      console.error("Error in dragOver:", error);
    }

    setDragOverItem(index);
  };

  const handleDragDrop = (e: React.DragEvent<HTMLTableRowElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (!draggable || dragItem === null || dragOverItem === null) return;

    // Don't allow dropping on non-draggable items
    const targetItem = localData[dragOverItem];
    if (targetItem.isDraggable === false) {
      setDragItem(null);
      setDragOverItem(null);
      return;
    }

    // Clear any drag over styling
    try {
      document.querySelectorAll('tr').forEach(row => {
        row.classList.remove('opacity-50', 'border-t-4', 'border-b-4', 'border-blue-500');
      });
    } catch (error) {
      console.error("Error cleaning up styles:", error);
    }

    // Don't do anything if the item is dropped at the same position
    if (dragItem === dragOverItem) {
      setDragItem(null);
      setDragOverItem(null);
      return;
    }

    console.log(`Reordering: Moving item from position ${dragItem} to position ${dragOverItem}`);

    // Create a copy of the data
    const newData = [...localData];
    const draggedItem = newData[dragItem];

    // Don't allow dragging a non-draggable item
    if (draggedItem.isDraggable === false) {
      setDragItem(null);
      setDragOverItem(null);
      return;
    }

    // Remove the item from its original position
    newData.splice(dragItem, 1);

    // Insert the item at the new position
    newData.splice(dragOverItem, 0, draggedItem);

    // Update col_order only for draggable items, preserving the order of non-draggable items
    let draggableIndex = 1;

    // Create the final array in the correct order
    const updatedData = newData.map(item => {
      if (item.isDraggable === false) {
        // Non-draggable items keep their existing col_order
        return item;
      } else {
        // Draggable items get sequential col_order values
        const newItem = {
          ...item,
          col_order: draggableIndex++
        };
        return newItem;
      }
    });

    // Log the reordering details for debugging
    console.log("Original order:", localData.map(item => `${item.uuid}: ${item.col_order}`));
    console.log("New order:", updatedData.map(item => `${item.uuid}: ${item.col_order}`));

    // Check if the order has actually changed
    const orderActuallyChanged = !compareArrays(localData, updatedData);
    console.log("Order actually changed:", orderActuallyChanged);

    if (orderActuallyChanged) {
      // Update local state with the data including updated col_order values
      setLocalData(updatedData);

      // Force set the orderChanged flag to true
      console.log("Setting orderChanged to true after drag and drop");
      setOrderChanged(true);

      // Call onReorder callback if provided
      if (onReorder) {
        onReorder(updatedData);
      }
    }

    // Reset drag states
    setDragItem(null);
    setDragOverItem(null);
  };

  const handleDragEnd = (e: React.DragEvent<HTMLTableRowElement>) => {
    // Clear any styling applied during dragging
    try {
      if (e.currentTarget) {
        e.currentTarget.classList.remove('opacity-50');
      }

      document.querySelectorAll('tr').forEach(row => {
        row.classList.remove('opacity-50', 'border-t-4', 'border-b-4', 'border-blue-500');
      });
    } catch (error) {
      console.error("Error cleaning up styles on drag end:", error);
    }

    setDragItem(null);
    setDragOverItem(null);
  };

  const handleDelete = (row: T): void => {
    setDeleteModalState({
      isOpen: true,
      itemToDelete: row
    });
  };

  const handleCloseDeleteModal = (): void => {
    setDeleteModalState({
      isOpen: false,
      itemToDelete: null
    });
  };

  // Function to save column order
  const handleSaveColumnOrder = async (): Promise<void> => {
    if (!onSaveColumnOrder) return;

    setIsSaving(true);
    try {
      // Important: Use the original data prop to ensure ALL columns are included
      // regardless of current search/filter state
      const allData = data;

      // Create a map of UUID to new position from current view
      const visibleOrderMap = new Map();
      localData.forEach((item, index) => {
        visibleOrderMap.set(item.uuid, index + 1);
      });

      // Create the order data with ALL columns
      const orderData = {
        columns: allData.map(item => {
          // Use the new order for visible items, maintain existing order for others
          const newOrder = visibleOrderMap.has(item.uuid)
            ? visibleOrderMap.get(item.uuid)
            : (typeof item.col_order === 'number' ? item.col_order : 0);

          return {
            uuid: item.uuid,
            col_order: newOrder
          };
        })
      };

      console.log("Saving ALL columns. Total count:", orderData.columns.length);
      console.log("Currently visible columns:", localData.length);

      // Call the provided save function with ALL columns
      await onSaveColumnOrder(orderData);

      // Reset the order changed flag after successful save
      setOrderChanged(false);
    } catch (error) {
      console.error('Error saving column order:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  return (
    <>
      <div className={`space-y-2 ${className} mt-2`}>
        <div className="flex justify-end gap-2 items-center">
          {onSearch && (
            <div className="flex px-4 items-center justify-center background-container border rounded-md">
              <Search className="text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search..."
                className="pl-2 pr-4 py-2 focus:outline-none background-container"
                value={localSearchTerm}
                onChange={handleSearch}
              />
            </div>
          )}

          {/* Save Order Button - always shown when draggable is true */}
          {draggable && onSaveColumnOrder && orderChanged && (
            <Button
              btnStyle="brandBtn"
              className="flex items-center gap-2 px-4 py-1.5"
              onClick={handleSaveColumnOrder}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save Order'}
            </Button>
          )}

        </div>

        <div className="overflow-x-auto border rounded-lg">
          <table className="min-w-full divide-y divide-gray-200 background-elements">
            <thead className="bg-gray-100 dark:bg-gray-800">
              <tr>
                {draggable && (
                  <th className="w-12 px-2 py-3"></th>
                )}
                {columns.map(column => (
                  <th
                    key={String(column.key)}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  >
                    <div className="flex items-center gap-2">
                      {column.label}
                      {column.sortable && sortConfig && (
                        sortConfig.key === column.key
                          ? (sortConfig.direction === 'asc'
                            ? <ChevronUp className="h-4 w-4" />
                            : <ChevronDown className="h-4 w-4" />
                          )
                          : <ArrowUpDown className="h-4 w-4" />
                      )}
                    </div>
                  </th>
                ))}
                {(onEdit || onDelete) && (
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200">
              {localData.map((row, index) => (
                <tr
                  key={row.uuid || index}
                  className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  draggable={draggable && row.isDraggable !== false}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDrop={handleDragDrop}
                  onDragEnd={handleDragEnd}
                  onDragEnter={(e) => {
                    if (draggable) e.preventDefault();
                  }}
                  onDragLeave={(e) => {
                    if (draggable && e.currentTarget) {
                      try {
                        e.currentTarget.classList.remove('border-t-4', 'border-b-4', 'border-blue-500');
                      } catch (error) {
                        console.error("Error in dragLeave:", error);
                      }
                    }
                  }}
                >
                  {draggable && (
                    <td className="w-12 px-2 py-3 text-center">
                      {row.isDraggable === false ? (
                        <div className="text-gray-400">
                          <Lock className="h-5 w-5" />
                        </div>
                      ) : (
                        <div className="cursor-grab text-gray-400">
                          <GripVertical className="h-5 w-5" />
                        </div>
                      )}
                    </td>
                  )}
                  {columns.map((column) => (
                    <td
                      key={String(column.key)}
                      className="px-4 py-3 text-sm text-gray-900 dark:text-gray-200 whitespace-nowrap"
                    >
                      {column.render
                        ? column.render(row[column.key], row)
                        : String(row[column.key] ?? '-')}
                    </td>
                  ))}
                  {(onEdit || onDelete) && (
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end space-x-2">
                        {onEdit && (
                          <button
                            onClick={() => onEdit(row)}
                            className="text-brandColor hover:text-blue-800 p-1 rounded"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                        )}
                        {onDelete && (
                          <button
                            onClick={() => handleDelete(row)}
                            className="text-red-600 hover:text-red-800 p-1 rounded"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {totalCount > itemsPerPage && onPageChange && (
          <div className="mt-4">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={onPageChange}
            />
          </div>
        )}
      </div>

      {deleteModalState.itemToDelete && (
        <DeleteConfirmationModal
          entityId={deleteModalState.itemToDelete.uuid}
          entityType="role"
          deleteFunction={async () => {
            if (onDelete && deleteModalState.itemToDelete) {
              await onDelete(deleteModalState.itemToDelete);
            }
          }}
          isOpen={deleteModalState.isOpen}
          onClose={handleCloseDeleteModal}
        />
      )}
    </>
  );
};

export default SortableTable;